import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer, Share2, ArrowLeft, Calendar, Users } from "lucide-react";
import { getPublicRetreatBill, fmt, fmtDate, fmtDateLong, LOGO_URL, BRAND_NAME } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABEL = { prepayment: "Prepayment", final: "Final", custom: "Custom" };
const STATUS_LABEL = {
  planned: "Planned",
  confirmed: "Confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function RetreatBill() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicRetreatBill(id)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const publicUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/retreat-bill/${id}`;
  }, [id]);

  const handlePrint = () => window.print();

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Account summary · ${data?.name || "Retreat"}`,
          text: "Retreat account summary",
          url: publicUrl,
        });
      } else {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Account summary link copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading account summary…</div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Account summary not found.</div>;
  }

  const s = data.summary || {};
  const total = s.total || 0;
  const paid = s.paid || 0;
  const remaining = Math.max(0, s.remaining || 0);
  const prepaymentsPaid = s.prepayments_paid || 0;
  const otherPaid = Math.max(0, paid - prepaymentsPaid);
  const paidFull = total > 0 && remaining <= 0.01;

  const today = fmtDateLong(new Date().toISOString().slice(0, 10));

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Top actions — hidden on print */}
      <div className="print:hidden bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/partners")}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
          data-testid="retreat-bill-back-btn"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleShare} className="h-10" data-testid="retreat-bill-share-btn">
            <Share2 size={14} /> Share
          </Button>
          <Button onClick={handlePrint} className="h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="retreat-bill-print-btn">
            <Printer size={14} /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-[800px] mx-auto my-6 bg-white shadow-sm print:shadow-none print:my-0 print:max-w-none" data-testid="retreat-bill-sheet">
        <div className="p-10 print:p-8">
          {/* Header */}
          <div className="flex items-start justify-between pb-6 border-b-2 border-slate-900">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-md bg-slate-950 flex items-center justify-center p-1.5 shrink-0">
                <img src={LOGO_URL} alt={BRAND_NAME} className="h-full w-full object-contain" />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl text-slate-900 leading-tight">{BRAND_NAME}</div>
                <div className="text-xs text-slate-500 mt-1">Retreat Account Summary</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500">Account Summary</div>
              <div className="font-heading font-bold text-lg text-slate-900" data-testid="retreat-bill-id">#{data.id.slice(0, 8).toUpperCase()}</div>
              <div className="text-xs text-slate-500 mt-0.5">{today}</div>
            </div>
          </div>

          {/* Partner + retreat header */}
          <div className="grid grid-cols-3 gap-6 py-6 border-b border-slate-200">
            <div className="col-span-2 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Partner</div>
                <div className="font-heading font-bold text-xl text-slate-900" data-testid="retreat-bill-partner">{data.partner?.name || "—"}</div>
                {data.partner?.contact && (
                  <div className="text-sm text-slate-600 mt-0.5">{data.partner.contact}</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Retreat</div>
                <div className="font-medium text-slate-900" data-testid="retreat-bill-name">{data.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{STATUS_LABEL[data.status] || data.status}</div>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-1">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Start</div>
                  <div className="text-sm font-medium text-slate-900 mt-1 flex items-center gap-1.5"><Calendar size={12} className="text-slate-400" /> {fmtDate(data.start_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">End</div>
                  <div className="text-sm font-medium text-slate-900 mt-1 flex items-center gap-1.5"><Calendar size={12} className="text-slate-400" /> {fmtDate(data.end_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Participants</div>
                  <div className="text-sm font-medium text-slate-900 mt-1 flex items-center gap-1.5"><Users size={12} className="text-slate-400" /> {data.participants}</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end justify-start">
              <div className="p-2 border border-slate-200 rounded-md bg-white">
                <QRCodeCanvas value={publicUrl} size={96} level="M" />
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5 max-w-[120px] text-right leading-tight">Scan to open this summary on any device</div>
            </div>
          </div>

          {/* Cost breakdown */}
          <div className="py-6">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Cost Breakdown</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 font-medium text-slate-500 text-xs uppercase tracking-wider">Item</th>
                  <th className="text-right py-2 font-medium text-slate-500 text-xs uppercase tracking-wider w-32">Amount</th>
                </tr>
              </thead>
              <tbody data-testid="retreat-bill-costs">
                {(data.costs || []).length === 0 && (
                  <tr><td colSpan={2} className="py-4 text-center text-slate-400 text-xs">No cost items</td></tr>
                )}
                {(data.costs || []).map(c => (
                  <tr key={c.id} className="border-b border-slate-100">
                    <td className="py-2.5 font-medium text-slate-900">{c.name}</td>
                    <td className="py-2.5 text-right font-medium text-slate-900 tabular-nums">{fmt(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end pb-6 border-b border-slate-200">
            <div className="w-72 space-y-1.5 text-sm" data-testid="retreat-bill-totals">
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="font-heading font-semibold text-slate-900">Total</span>
                <span className="font-heading font-bold text-slate-900 tabular-nums">{fmt(total)}</span>
              </div>
              {prepaymentsPaid > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Prepayment received</span>
                  <span className="tabular-nums">-{fmt(prepaymentsPaid)}</span>
                </div>
              )}
              {otherPaid > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Other payments received</span>
                  <span className="tabular-nums">-{fmt(otherPaid)}</span>
                </div>
              )}
              <div className={`flex justify-between pt-2 border-t-2 border-slate-900 ${paidFull ? "text-emerald-600" : "text-amber-700"}`}>
                <span className="font-heading font-bold">{paidFull ? "Paid in full" : "Balance due"}</span>
                <span className="font-heading font-bold text-lg tabular-nums">{fmt(remaining)}</span>
              </div>
            </div>
          </div>

          {/* Payment schedule / received */}
          {(data.payments || []).length > 0 && (
            <div className="py-6 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Payment schedule</div>
              <div className="space-y-1 text-sm" data-testid="retreat-bill-payments">
                {data.payments.map(p => (
                  <div key={p.id} className="flex justify-between">
                    <span className="text-slate-600">
                      <span className="font-medium text-slate-900">{TYPE_LABEL[p.type] || p.type}</span>
                      {p.due_date && <span className="text-slate-500"> · due {fmtDate(p.due_date)}</span>}
                      {p.paid && p.payment_date && <span className="text-emerald-700"> · paid {fmtDate(p.payment_date)}</span>}
                      {!p.paid && <span className="text-amber-700"> · pending</span>}
                    </span>
                    <span className="font-medium text-slate-900 tabular-nums">{fmt(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Account summary notice — printable */}
          <div className="pt-6" data-testid="retreat-bill-notice">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Important</div>
            <div className="text-xs text-slate-600 leading-relaxed">
              This document is an <span className="font-semibold text-slate-900">account summary</span>, <span className="font-semibold text-slate-900">NOT a billing invoice</span>.
              It is intended for internal reconciliation between {BRAND_NAME} and the partner. If you require a formal invoice for accounting purposes,
              please email{" "}
              <a href="mailto:info@ondinaguesthouse.com" className="font-semibold text-slate-900 underline">info@ondinaguesthouse.com</a>{" "}
              with your name, address, and tax file number.
            </div>
          </div>

          {/* Footer */}
          <div className="pt-8 mt-4 text-center text-xs text-slate-400 border-t border-slate-100">
            {publicUrl}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
